/**
 * Pure assembly of the {{variables}} payload for a supplier draft email
 * (gmail-create-draft). Extracted from OrderTrackerPage so it can be unit
 * tested without dragging in the page's eager `@/lib/supabase` import.
 *
 * The common fields cover perpedes_order_insole / local_provider_design; the
 * sandal branch adds the sandal_* fields from configuration.deep.sandal for
 * perpedes_order_sandal. Any template var with no source renders blank (the
 * edge interpolator treats missing vars as empty strings).
 *
 * Quantity: when the line carries more than one pair, product_name itself is
 * suffixed with "(N PAIRS)" so the count lands in the subject line AND the
 * Product bullet of the CURRENT template bodies with no email_templates
 * change. `quantity` / `quantity_block` are also provided for templates that
 * want a dedicated placeholder (quantity_block is pre-rendered HTML because
 * interpolate() has no conditionals; empty at qty 1, mirroring the
 * patient_list_html precedent in _shared/email-templates.ts).
 */
import type { OrderDetail } from '@insolech/sdk';
import { shortProductLabel } from './productLabel';

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}
function asStr(v: unknown): string {
  return v == null ? '' : String(v);
}

/**
 * Pre-rendered {{quantity_block}} HTML: a loud red bullet when more than one
 * pair is required, empty string otherwise (the interpolator emits nothing
 * for it). Exported so the admin template-editor preview (SAMPLE_VALUES)
 * renders exactly what real drafts produce.
 */
export function renderQuantityBlock(qty: number): string {
  return qty > 1
    ? `<li><strong style="color:#b91c1c;">Quantity: ${qty} PAIRS</strong></li>`
    : '';
}

export function buildDraftVariables(
  detail: OrderDetail,
  line: OrderDetail['lines'][number] | undefined,
  isSandal: boolean,
  /** window.location.origin at the call site; injected for testability. */
  origin: string,
): Record<string, string> {
  // Custom insoles have no products row (product_id null), so line.product is
  // null. The display name lives in solution_subtype; shortProductLabel maps
  // it to the full label (e.g. polypropylene_insole -> "Polypropylene insole").
  const baseProductName = line ? shortProductLabel(line) : '';
  const qty = line?.quantity ?? 1;
  const productName = qty > 1 ? `${baseProductName} (${qty} PAIRS)` : baseProductName;
  const base: Record<string, string> = {
    order_number: detail.order_number ?? '',
    // Order-scoped line name wins over the shared header record (mirrors
    // resolvePatientDisplay) so a per-order name correction reaches the draft.
    patient_name: (line?.patient_name?.trim() || detail.patient?.full_name) ?? '',
    product_name: productName,
    quantity: String(qty),
    quantity_block: renderQuantityBlock(qty),
    clinic_name: detail.clinic?.name ?? '',
    podiatrist_name: detail.podiatrist?.full_name ?? '',
    order_url: `${origin}/admin/orders/${detail.id}`,
  };
  if (!isSandal) return base;
  const cfg = asObj(line?.configuration);
  const sandal = asObj(asObj(cfg.deep).sandal);
  return {
    ...base,
    sandal_product_name: productName,
    sandal_size: asStr(cfg.size_eu ?? cfg.size),
    sandal_model: asStr(sandal.model),
    sandal_base_color: asStr(sandal.base_color),
    sandal_upper_strap_color: asStr(sandal.upper_strap_color),
    sandal_top_cover: asStr(sandal.top_cover),
    sandal_white_midsole: asStr(sandal.white_midsole),
    sandal_posterior_strap: asStr(sandal.posterior_strap),
    sandal_upper_strap_size: asStr(sandal.upper_strap_size),
    sandal_custom_name: asStr(sandal.custom_name),
  };
}
